import type { SupabaseClient } from '../supabase/client';
import type { Store, UpdateStoreContactMethodsInput, StoreContactErrorKey } from '../types/store';
import { normalizedContactMethodsSchema } from '../schemas/contact-methods';
import { getAdminStore } from './get-admin-store';

/**
 * F-007 — Update the store's `whatsapp_number` / `phone_number`
 * columns via the column-scoped `UPDATE` grant added in migration
 * `20260418000000_f007_contact_methods.sql`.
 *
 * The DB accepts both fields null (an in-progress form save may have
 * one field cleared and the other not yet typed). The authoritative
 * "at least one contact method" check is the publish gate
 * (`publish_store` RPC, SQLSTATE P0003). This service does NOT
 * re-enforces the at-least-one constraint for direct programmatic callers,
 * but it does not parse country/local UI objects. The admin form must
 * transform through `contactMethodsSchema` before calling this boundary.
 *
 * The update itself uses the column-scoped `stores` grant. The fresh
 * admin cache row is then read through `get_admin_store` so internal
 * settings that customers must not see never travel through direct table
 * SELECT grants.
 *
 * Design reference: `.dukanti/designs/F-007.md` §3.4 + §3.7.
 */
export async function updateStoreContactMethods(
  client: SupabaseClient,
  input: UpdateStoreContactMethodsInput,
): Promise<Store> {
  // Defensive validation for normalized E.164 callers. Country/local UI
  // parsing belongs to `contactMethodsSchema` at the form boundary.
  const parsed = normalizedContactMethodsSchema.parse({
    whatsappNumber: input.whatsappNumber,
    phoneNumber: input.phoneNumber,
  });

  const { data, error } = await client
    .from('stores')
    .update({
      whatsapp_number: parsed.whatsappNumber,
      phone_number: parsed.phoneNumber,
    })
    .eq('id', input.storeId)
    .select('id')
    .single();

  if (error) throw error;
  if (!data) throw new Error('updateStoreContactMethods: empty update result');
  return getAdminStore(client, input.storeId);
}

/**
 * F-007 — Whitelist error-to-translation-key mapper for contact-methods
 * updates. Parallel to `mapPublishError` / `mapStoreError`. Code-first,
 * matching F-004's "no regex fallbacks" discipline.
 *
 * Branches:
 *   1. null / undefined / non-object                    → unknown
 *   2. TypeError                                        → networkFailed
 *   3. name === 'AuthRetryableFetchError'               → networkFailed
 *   3b. fetch/load/abort/network-shaped message         → networkFailed
 *   4. code === '23514' + stores_whatsapp_e164_chk      → whatsappInvalid
 *   5. code === '23514' + stores_whatsapp_length_chk    → whatsappInvalid
 *   6. code === '23514' + stores_phone_e164_chk         → phoneInvalid
 *   7. code === '23514' + stores_phone_length_chk       → phoneInvalid
 *   8. anything else                                     → unknown
 *
 * Postgres raises SQLSTATE `23514` on CHECK constraint violations and
 * includes the constraint name in the message. We key on SQLSTATE +
 * constraint-name substring so a future message rewording doesn't
 * regress the mapping.
 */
export function mapStoreContactError(err: unknown): StoreContactErrorKey {
  if (!err || typeof err !== 'object') return 'admin.storeSettings.errors.unknown';

  const maybe = err as { code?: string; message?: string; name?: string };

  if (err instanceof TypeError || maybe.name === 'TypeError') {
    return 'admin.storeSettings.errors.networkFailed';
  }

  if (maybe.name === 'AuthRetryableFetchError') {
    return 'admin.storeSettings.errors.networkFailed';
  }

  if (/\b(fetch|network|abort(?:ed)?)\b|load failed/i.test(maybe.message ?? '')) {
    return 'admin.storeSettings.errors.networkFailed';
  }

  if (maybe.code === '23514') {
    if (
      maybe.message?.includes('stores_whatsapp_e164_chk') ||
      maybe.message?.includes('stores_whatsapp_length_chk')
    ) {
      return 'admin.storeSettings.errors.whatsappInvalid';
    }
    if (
      maybe.message?.includes('stores_phone_e164_chk') ||
      maybe.message?.includes('stores_phone_length_chk')
    ) {
      return 'admin.storeSettings.errors.phoneInvalid';
    }
  }

  return 'admin.storeSettings.errors.unknown';
}
